/* ─── BURNS DESIGN — THEME JS ─────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ─── CART STATE ──────────────────────────────────────────────────────────── */
  let cart = null;

  async function fetchCart() {
    const res = await fetch('/cart.js');
    cart = await res.json();
    return cart;
  }

  async function addToCart(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Could not add to cart');
    await fetchCart();
    updateCartUI();
    openCartDrawer();
  }

  async function removeCartItem(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    await fetchCart();
    updateCartUI();
  }

  /* ─── CART DRAWER UI ──────────────────────────────────────────────────────── */
  function openCartDrawer() {
    document.getElementById('cart-drawer').classList.add('cart-drawer--open');
    document.getElementById('cart-overlay').classList.add('cart-overlay--open');
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    document.getElementById('cart-drawer').classList.remove('cart-drawer--open');
    document.getElementById('cart-overlay').classList.remove('cart-overlay--open');
    document.body.style.overflow = '';
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function updateCartUI() {
    if (!cart) return;

    // Update header cart badge
    const countEls = document.querySelectorAll('[data-cart-count]');
    countEls.forEach(el => {
      el.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';
      el.style.display = cart.item_count > 0 ? 'inline' : 'none';
    });
    // Update cart drawer title count
    const drawerCount = document.getElementById('cart-drawer-count');
    if (drawerCount) drawerCount.textContent = cart.item_count;

    // Update cart drawer items
    const cartInner = document.getElementById('cart-inner');
    const cartFooter = document.getElementById('cart-footer');
    if (!cartInner) return;

    if (cart.items.length === 0) {
      cartInner.innerHTML = '<div class="cart-empty">Your collection is empty.</div>';
      if (cartFooter) cartFooter.style.display = 'none';
      return;
    }

    if (cartFooter) cartFooter.style.display = 'block';

    cartInner.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__thumb">
          ${item.image
            ? `<img src="${item.image}" alt="${item.title}" style="width:72px;height:72px;object-fit:cover;">`
            : `<div style="width:72px;height:72px;background:var(--gray-700);display:flex;align-items:center;justify-content:center;">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--gray-500)" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="1"/><path d="m3 9 4-4 4 4 4-4 4 4"/></svg>
              </div>`
          }
        </div>
        <div class="cart-item__body">
          <div class="cart-item__category">${item.product_type || ''}</div>
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${formatMoney(item.final_price)}</div>
        </div>
        <button class="cart-item__remove" data-remove-key="${item.key}" aria-label="Remove">✕</button>
      </div>
    `).join('');

    // Total
    const totalEl = document.getElementById('cart-total');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price) + '+';

    // Remove buttons
    cartInner.querySelectorAll('[data-remove-key]').forEach(btn => {
      btn.addEventListener('click', () => removeCartItem(btn.dataset.removeKey));
    });
  }

  /* ─── QUICK ADD BUTTONS ───────────────────────────────────────────────────── */
  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.quickAdd;
      if (!variantId || btn.dataset.sold === 'true') return;

      const originalText = btn.textContent;
      btn.textContent = 'ADDED TO CART';
      btn.classList.add('quick-add-btn--added');

      try {
        await addToCart(variantId);
      } catch (err) {
        console.error(err);
      }

      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('quick-add-btn--added');
      }, 1600);
    });
  }

  /* ─── ADD TO CART FORM (product page) ────────────────────────────────────── */
  function initAddToCartForm() {
    const form = document.getElementById('product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput || !variantInput.value) return;

      const btn = form.querySelector('[data-add-to-cart-btn]');
      if (btn) {
        btn.textContent = 'ADDED TO CART ✓';
        btn.classList.add('add-to-cart-btn--added');
      }

      try {
        await addToCart(variantInput.value);
      } catch (err) {
        console.error(err);
      }

      setTimeout(() => {
        if (btn) {
          btn.textContent = 'ADD TO CART';
          btn.classList.remove('add-to-cart-btn--added');
        }
      }, 2000);
    });
  }

  /* ─── VARIANT SELECTORS (product page) ───────────────────────────────────── */
  function initVariantSelectors() {
    const selectors = document.querySelectorAll('[data-variant-option]');
    if (!selectors.length) return;

    const variantData = JSON.parse(
      document.getElementById('product-variants-json')?.textContent || '[]'
    );

    function getSelectedOptions() {
      const groups = {};
      selectors.forEach(sel => {
        const idx = sel.dataset.optionIndex;
        // Register this group even if nothing is active yet
        if (!(idx in groups)) groups[idx] = null;
        // Only record the button that is actually selected
        if (sel.classList.contains('variant-btn--active') ||
            sel.classList.contains('format-btn--active')) {
          groups[idx] = sel.dataset.optionValue;
        }
      });
      // Return values sorted by option index (0, 1, 2...) to match variant.options order
      return Object.keys(groups)
        .sort((a, b) => Number(a) - Number(b))
        .map(k => groups[k]);
    }

    function findVariant(selectedOpts) {
      return variantData.find(v =>
        v.options.every((opt, i) => opt === selectedOpts[i])
      ) || null;
    }

    function updateSelectedStyles() {
      const groups = {};
      selectors.forEach(sel => {
        const group = sel.dataset.optionIndex;
        if (!groups[group]) groups[group] = [];
        groups[group].push(sel);
      });
      Object.values(groups).forEach(group => {
        // handled via active class in click handler
      });
    }

    selectors.forEach(sel => {
      sel.addEventListener('click', () => {
        const group = sel.dataset.optionIndex;
        // Deactivate siblings in same group
        document.querySelectorAll(`[data-option-index="${group}"]`).forEach(s => {
          s.classList.remove('variant-btn--active', 'format-btn--active');
          const check = s.querySelector('.format-btn__check');
          if (check) check.style.display = 'none';
        });
        sel.classList.add(sel.classList.contains('format-btn') ? 'format-btn--active' : 'variant-btn--active');
        const check = sel.querySelector('.format-btn__check');
        if (check) check.style.display = 'inline';

        const selectedOpts = getSelectedOptions();
        const variant = findVariant(selectedOpts);

        const variantInput = document.querySelector('[name="id"]');
        if (variantInput && variant) {
          variantInput.value = variant.id;
        }

        // Update price display
        if (variant) {
          const priceEl = document.getElementById('product-price');
          if (priceEl) priceEl.textContent = formatMoney(variant.price);
        }

        // Update main media to variant's image if available
        if (variant && variant.featured_image) {
          const mainImg = document.getElementById('product-main-image');
          const mainVid = document.getElementById('product-main-video');
          if (mainImg) { mainImg.src = variant.featured_image.src; mainImg.style.display = 'block'; }
          if (mainVid) { mainVid.pause(); mainVid.style.display = 'none'; }
        }
      });
    });
  }

  /* ─── COLLECTION FILTER (JS-based on collection pages) ───────────────────── */
  function initCollectionFilter() {
    const filterBtns = document.querySelectorAll('[data-filter-handle]');
    if (!filterBtns.length) return;

    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.filterHandle;
        if (handle === 'all') {
          window.location.href = '/collections/all';
        } else {
          window.location.href = `/collections/${handle}`;
        }
      });
    });
  }

  /* ─── NEWSLETTER FORM ─────────────────────────────────────────────────────── */
  function initNewsletter() {
    const form = document.getElementById('newsletter-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = form.querySelector('[name="contact[email]"]')?.value;
      if (!email) return;

      try {
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(new FormData(form))
        });
        if (res.ok) {
          const successEl = document.getElementById('newsletter-success');
          if (successEl) {
            form.style.display = 'none';
            successEl.style.display = 'block';
          }
        }
      } catch (err) {
        console.error(err);
      }
    });
  }

  /* ─── SEARCH OVERLAY ─────────────────────────────────────────────────────── */
  function initSearch() {
    const overlay   = document.getElementById('search-overlay');
    const openBtn   = document.getElementById('search-open-btn');
    const closeBtn  = document.getElementById('search-close-btn');
    const input     = document.getElementById('search-overlay-input');
    if (!overlay || !openBtn) return;

    function openSearch() {
      overlay.classList.add('search-overlay--open');
      overlay.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input && input.focus(), 80);
    }

    function closeSearch() {
      overlay.classList.remove('search-overlay--open');
      overlay.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    openBtn.addEventListener('click', openSearch);
    closeBtn && closeBtn.addEventListener('click', closeSearch);

    // Click outside the inner panel to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeSearch();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('search-overlay--open')) closeSearch();
    });
  }

  /* ─── MOBILE MENU ─────────────────────────────────────────────────────────── */
  function initMobileMenu() {
    const btn = document.getElementById('mobile-menu-btn');
    const menu = document.getElementById('mobile-menu');
    if (!btn || !menu) return;

    btn.addEventListener('click', () => {
      const isOpen = menu.style.display === 'block';
      menu.style.display = isOpen ? 'none' : 'block';
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  /* ─── STICKY HEADER SHRINK ────────────────────────────────────────────────── */
  function initStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    // Header is already sticky via CSS; no JS needed unless we want scroll effects
  }

  /* ─── PRODUCT IMAGE GALLERY (product page) ───────────────────────────────── */
  function initProductGallery() {
    const thumbs     = document.querySelectorAll('[data-gallery-thumb]');
    const mainImg    = document.getElementById('product-main-image');
    const mainVid    = document.getElementById('product-main-video');
    const strip      = document.getElementById('product-thumbnails');
    const prevBtn    = document.getElementById('thumb-prev');
    const nextBtn    = document.getElementById('thumb-next');
    const mainBadge  = document.getElementById('main-draft-badge');

    function showImage(src) {
      if (mainImg) { mainImg.src = src; mainImg.style.display = 'block'; }
      if (mainVid) { mainVid.pause(); mainVid.style.display = 'none'; }
    }

    function showVideo(url) {
      if (!mainVid || !url) return;
      mainVid.innerHTML = `<source src="${url}" type="video/mp4">`;
      mainVid.load();
      mainVid.style.display = 'block';
      mainVid.play().catch(() => {});
      if (mainImg) mainImg.style.display = 'none';
    }

    // Thumbnail click → swap main media
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('gallery-thumb--active'));
        thumb.classList.add('gallery-thumb--active');
        if (thumb.dataset.mediaType === 'video') {
          showVideo(thumb.dataset.videoUrl);
        } else {
          showImage(thumb.dataset.galleryThumb);
        }
        if (mainBadge) mainBadge.style.display = thumb.dataset.isDraft === 'true' ? 'block' : 'none';
      });
    });

    // Arrow buttons → scroll thumbnail strip
    if (strip) {
      const scrollAmount = 210;
      prevBtn?.addEventListener('click', () => strip.scrollBy({ left: -scrollAmount, behavior: 'smooth' }));
      nextBtn?.addEventListener('click', () => strip.scrollBy({ left: scrollAmount, behavior: 'smooth' }));

      function updateArrows() {
        const atStart = strip.scrollLeft <= 0;
        const atEnd = strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 1;
        if (prevBtn) { prevBtn.style.opacity = atStart ? '0' : '1'; prevBtn.style.pointerEvents = atStart ? 'none' : 'auto'; }
        if (nextBtn) { nextBtn.style.opacity = atEnd ? '0' : '1'; nextBtn.style.pointerEvents = atEnd ? 'none' : 'auto'; }
      }
      strip.addEventListener('scroll', updateArrows);
      updateArrows();
    }
  }

  /* ─── QUOTE MODAL ───────────────────────────────────────────────────────── */
  function initQuoteModal() {
    const modal     = document.getElementById('quote-modal');
    if (!modal) return;
    const backdrop  = modal.querySelector('.quote-modal__backdrop');
    const closeBtn  = modal.querySelector('.quote-modal__close');
    const formWrap  = document.getElementById('quote-form-wrap');
    const form      = document.getElementById('quote-form');
    const successEl = document.getElementById('quote-success');
    const textarea  = document.getElementById('qf-message');
    const charCount = document.getElementById('qf-char-count');
    const nameFld   = document.getElementById('qf-name-combined');

    function openModal() {
      modal.classList.add('quote-modal--open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      formWrap.style.display = '';
      successEl.style.display = 'none';
    }
    function closeModal() {
      modal.classList.remove('quote-modal--open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    // Open on any .svc-cta-btn or .inquiry-cta-btn click
    document.addEventListener('click', e => {
      if (e.target.closest('.svc-cta-btn') || e.target.closest('.inquiry-cta-btn')) { e.preventDefault(); openModal(); }
    });

    backdrop.addEventListener('click', closeModal);
    closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.classList.contains('quote-modal--open')) closeModal();
    });

    // Character counter
    textarea?.addEventListener('input', () => {
      if (charCount) charCount.textContent = textarea.value.length;
    });

    // Submit via fetch so the page never navigates away
    form?.addEventListener('submit', async e => {
      e.preventDefault();
      const first = document.getElementById('qf-first')?.value.trim() || '';
      const last  = document.getElementById('qf-last')?.value.trim()  || '';
      if (nameFld) nameFld.value = [first, last].filter(Boolean).join(' ');

      const submitBtn = form.querySelector('.quote-form__submit');
      submitBtn.textContent = 'Sending…';
      submitBtn.disabled = true;

      try {
        const body = new URLSearchParams(new FormData(form)).toString();
        await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body
        });
        formWrap.style.display = 'none';
        successEl.style.display = 'block';
        form.reset();
        if (charCount) charCount.textContent = '0';
      } catch {
        submitBtn.textContent = 'Send Request →';
        submitBtn.disabled = false;
      }
    });
  }

  /* ─── IMAGE PROTECTION ───────────────────────────────────────────────────── */
  function initImageProtection() {
    // Block right-click context menu on all images
    document.addEventListener('contextmenu', e => {
      if (e.target.tagName === 'IMG') e.preventDefault();
    });
    // Block drag-to-desktop saving
    document.addEventListener('dragstart', e => {
      if (e.target.tagName === 'IMG') e.preventDefault();
    });
    // Block Ctrl/Cmd+S page save
    document.addEventListener('keydown', e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') e.preventDefault();
    });
    // Set draggable=false on all current and future images via MutationObserver
    function protectImages(root) {
      root.querySelectorAll('img').forEach(img => {
        img.setAttribute('draggable', 'false');
      });
    }
    protectImages(document);
    new MutationObserver(mutations => {
      mutations.forEach(m => m.addedNodes.forEach(node => {
        if (node.nodeType === 1) protectImages(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ─── LIGHTBOX ────────────────────────────────────────────────────────────── */
  function initLightbox() {
    const lightbox  = document.getElementById('lightbox');
    if (!lightbox) return;

    const lbImg      = document.getElementById('lb-img');
    const stage      = document.getElementById('lb-stage');
    const zoomInBtn  = document.getElementById('lb-zoom-in');
    const zoomOutBtn = document.getElementById('lb-zoom-out');
    const scaleEl    = document.getElementById('lb-scale');
    const closeBtn   = document.getElementById('lb-close');
    const backdrop   = document.getElementById('lb-backdrop');
    const prevBtn    = document.getElementById('lb-prev');
    const nextBtn    = document.getElementById('lb-next');

    let scale = 1, tx = 0, ty = 0;
    let dragging = false, dx = 0, dy = 0, startTx = 0, startTy = 0;
    const MIN = 0.5, MAX = 8, STEP = 0.5;
    let images = [], currentIndex = 0;

    function buildImageList() {
      images = Array.from(
        document.querySelectorAll('.product-detail-thumb:not(.thumb--video)[data-gallery-thumb]')
      ).map(el => el.getAttribute('data-gallery-thumb'));
      if (!images.length) {
        const mainImg = document.getElementById('product-main-image');
        if (mainImg && mainImg.src) images = [mainImg.src];
      }
    }

    function updateNav() {
      const show = images.length > 1;
      if (prevBtn) prevBtn.hidden = !show;
      if (nextBtn) nextBtn.hidden = !show;
    }

    function applyTransform(animate) {
      lbImg.style.transition = animate ? 'transform 0.18s ease' : 'none';
      lbImg.style.transform  = `translate(${tx}px,${ty}px) scale(${scale})`;
      scaleEl.textContent    = Math.round(scale * 100) + '%';
    }

    function openLightbox(src) {
      buildImageList();
      currentIndex = images.indexOf(src);
      if (currentIndex < 0) currentIndex = 0;
      lbImg.src = images[currentIndex] || src;
      scale = 1; tx = 0; ty = 0;
      applyTransform(false);
      updateNav();
      lightbox.classList.add('lightbox--open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      lightbox.classList.remove('lightbox--open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(() => { lbImg.src = ''; }, 200);
    }

    function navigate(dir) {
      if (images.length <= 1) return;
      currentIndex = (currentIndex + dir + images.length) % images.length;
      lbImg.src = images[currentIndex];
      scale = 1; tx = 0; ty = 0;
      applyTransform(false);
      stage.style.cursor = 'default';
    }

    function setZoom(newScale, animate) {
      scale = Math.min(MAX, Math.max(MIN, newScale));
      if (scale <= 1) { tx = 0; ty = 0; }
      applyTransform(animate !== false);
      stage.style.cursor = scale > 1 ? 'grab' : 'default';
    }

    // Open on main image click — skip if video is currently showing
    const mainWrap = document.querySelector('.product-main-image-wrap');
    if (mainWrap) {
      mainWrap.addEventListener('click', () => {
        const vid = document.getElementById('product-main-video');
        if (vid && vid.style.display !== 'none' && vid.querySelector('source')) return;
        const img = document.getElementById('product-main-image');
        if (img && img.src && img.style.display !== 'none') openLightbox(img.src);
      });
    }

    // Zoom buttons
    zoomInBtn.addEventListener('click',  () => setZoom(scale + STEP));
    zoomOutBtn.addEventListener('click', () => setZoom(scale - STEP));

    // Nav buttons
    if (prevBtn) prevBtn.addEventListener('click', e => { e.stopPropagation(); navigate(-1); });
    if (nextBtn) nextBtn.addEventListener('click', e => { e.stopPropagation(); navigate(1); });

    // Scroll wheel zoom
    stage.addEventListener('wheel', e => {
      e.preventDefault();
      setZoom(scale + (e.deltaY < 0 ? STEP : -STEP));
    }, { passive: false });

    // Drag to pan (mouse)
    stage.addEventListener('mousedown', e => {
      if (scale <= 1) return;
      if (e.target === prevBtn || e.target === nextBtn) return;
      dragging = true;
      dx = e.clientX; dy = e.clientY;
      startTx = tx; startTy = ty;
      stage.classList.add('lightbox__stage--grabbing');
    });
    document.addEventListener('mousemove', e => {
      if (!dragging) return;
      tx = startTx + (e.clientX - dx);
      ty = startTy + (e.clientY - dy);
      applyTransform(false);
    });
    document.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('lightbox__stage--grabbing');
    });

    // Drag to pan (touch)
    let touch0 = null;
    stage.addEventListener('touchstart', e => {
      if (e.touches.length === 1 && scale > 1) {
        touch0 = e.touches[0];
        startTx = tx; startTy = ty;
      }
    }, { passive: true });
    stage.addEventListener('touchmove', e => {
      if (e.touches.length === 1 && touch0 && scale > 1) {
        e.preventDefault();
        tx = startTx + (e.touches[0].clientX - touch0.clientX);
        ty = startTy + (e.touches[0].clientY - touch0.clientY);
        applyTransform(false);
      }
    }, { passive: false });
    stage.addEventListener('touchend', () => { touch0 = null; });

    // Close + keyboard navigation
    closeBtn.addEventListener('click', closeLightbox);
    backdrop.addEventListener('click', closeLightbox);
    document.addEventListener('keydown', e => {
      if (!lightbox.classList.contains('lightbox--open')) return;
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    });
  }

  /* ─── INIT ────────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', async () => {
    // Cart toggle
    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', openCartDrawer);
    });
    document.getElementById('cart-overlay')?.addEventListener('click', closeCartDrawer);
    document.getElementById('cart-close-btn')?.addEventListener('click', closeCartDrawer);

    // Load initial cart state
    try {
      await fetchCart();
      updateCartUI();
    } catch (e) {
      console.warn('Cart fetch failed', e);
    }

    initQuickAdd();
    initAddToCartForm();
    initVariantSelectors();
    initCollectionFilter();
    initNewsletter();
    initSearch();
    initMobileMenu();
    initStickyHeader();
    initProductGallery();
    initLightbox();
    initImageProtection();
    initQuoteModal();

    // Escape key closes cart (lightbox has its own handler)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeCartDrawer();
    });
  });

})();
